import { DunningLevel, NgbOffcanvas, NotificationService, callStated, toDateOnly, withCallState } from '@aston/foundation';
import { catchError, of, Subject, switchMap, take, takeUntil, tap, throwError } from 'rxjs';
import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { inject, Signal } from '@angular/core';
import { Router } from '@angular/router';

import { DunningTimelineExportComponent, TimelineExportSettings } from '../../components/dunning-timeline-export/dunning-timeline-export.component';
import { getDunningLevelsBitAndValue } from '../../functions/bitwise-filters.functions';
import { DunningLevelModelApi } from '../../../accounting-module/models/api';
import { WebSocketService } from '../../../shared-module/services';
import { WsEventType } from '../../../shared-module/enums';
import { DunningTimelineService } from '../../services';
import { AppConstants } from '../../../app.constants';
import * as R from '../../../routes-definitions';

const TIMELINE_EXPORT = 'timelineExport'

export function withTimelineExport<_>() {
	return signalStoreFeature(
		{
			state: type<{
				superDebtorId: number,
				selectedDunningLevels: DunningLevel[],
			}>(),
			props: type<{
				dunningLevels: Signal<DunningLevelModelApi[]>,
			}>(),
		},

		withCallState({ collection: TIMELINE_EXPORT }),

		withMethods(store => {
			const tlService = inject(DunningTimelineService)
			const notify = inject(NotificationService)
			const offcanvas = inject(NgbOffcanvas)
			const ws = inject(WebSocketService)
			const modals = inject(NgbModal)
			const router = inject(Router)

			const listenForEvent = (): Subject<any> => {
				const cancel$ = new Subject()
				ws.ofType(WsEventType.SuperDebtorTimelineCompleted).pipe(take(1), takeUntil(cancel$)).subscribe(_ => {
					notify.reset()
					setTimeout(() => notify.success({
						message: 'TopBar.Notifications.Settings.Tooltip.SuperDebtorTimelineCompleted',
						options: {
							autoCloseDelay: 10000,
							undoText: 'Actions.Preview',
							undoAction: () => {
								notify.reset()
								router.navigateByUrl(R.getSuperDebtorDetailFullPath(store.superDebtorId(), R.SuperDebtorDetailLinkedDocumentsRoutePath));
							}
						}
					}), 300)
				})
				return cancel$;
			}

			const exportTimeline = (form: TimelineExportSettings) => {
				const cancel$ = listenForEvent();
				return callStated(
					of(null).pipe(
						switchMap(_ => tlService.exportTimelineForSuperDebtor(
							store.superDebtorId(),
							getDunningLevelsBitAndValue(form.dunningLevels, store.dunningLevels()),
							toDateOnly(form.startDate),
							toDateOnly(form.endDate)
						)
							.pipe(
								tap(count => {
									if (count) {
										notify.success({ message: 'Actions.PreparingFile' })
									} else {
										notify.warning({ message: 'Errors.NoAvailableData' })
										cancel$.next(null)
										cancel$.complete()
									}
								}),
								tap(_ => modals.dismissAll()),
							)),
						catchError(e => {
							cancel$.next(null)
							cancel$.complete()
							return throwError(() => e)
						}),
					),
					store,
					TIMELINE_EXPORT
				)
			}

			const openTimelineExportSettings = (settings: Partial<TimelineExportSettings> = {}) => {
				const ref = modals.open(DunningTimelineExportComponent, {
					...AppConstants.DEFAULT_MODAL_OPTIONS,
				})
				router.events.pipe(takeUntil(ref.closed)).subscribe(() => offcanvas.dismiss());
				const modalInstance: DunningTimelineExportComponent = ref.componentInstance
				modalInstance.fillValues(settings)
				modalInstance.submitForm.subscribe(data => exportTimeline(data))
			}

			return {
				openTimelineExportSettings,
			}
		}),
	);
}

